import {Component, Input} from '@angular/core';
import {CommonModule} from '@angular/common';
import {Router} from '@angular/router';
import {
    TimelineBehaviour,
    TimelineCheckin,
    TimelineData,
    TimelineDay
} from "@shared/api/models/query/patient-statistics.model";

/**
 * Behaviours line state (UNIVAPLAT-2377, HAZ-B three-state contract):
 * - 'logged'     -> behaviours array is present and has at least one NONZERO
 *                   counter (severity pills — zero-counter entries are
 *                   filtered out, see the `behaviours` getter, so a day
 *                   where the backend sends a dense array of all-zero
 *                   categories reads as "None today" just like an
 *                   explicit empty array)
 * - 'none-today' -> behaviours array is present but empty, or every entry is
 *                   zero (explicit positive "None today" answer — clinically
 *                   distinct from missing data)
 * - 'missing'    -> behaviours key absent for the day, or the day has no data
 *                   at all ("No check-in" for past dates / "Not yet today" today)
 */
export type BehavioursState = 'logged' | 'none-today' | 'missing';

@Component({
    selector: 'cdss-patient-overview-v2-timeline-card',
    imports: [CommonModule],
    templateUrl: './patient-overview-v2-timeline-card.component.html',
    standalone: true,
    styleUrl: './patient-overview-v2-timeline-card.component.scss'
})
export class PatientOverviewV2TimelineCardComponent {

    /** Fixed render order of the daily check-in icons. */
    readonly checkinNames: TimelineCheckin['name'][] = ['Stress', 'Mood', 'Sleep'];

    @Input() type: 'patient' | 'carer' = 'patient';
    @Input() patientId: number | string = null;

    private _timeline: TimelineData = null;
    private _day: Date = null;

    /** The day slice of the timeline for this card, null when the day is absent. */
    item: TimelineDay = null;

    constructor(private router: Router) {}

    @Input()
    set day(day: Date) {
        this._day = day;
        this.refreshItem();
    }

    get day(): Date {
        return this._day;
    }

    @Input()
    set timeline(timeline: TimelineData) {
        this._timeline = timeline;
        this.refreshItem();
    }

    get timeline(): TimelineData {
        return this._timeline;
    }

    private refreshItem(): void {
        if (!this._timeline || !this._day) {
            this.item = null;
            return;
        }
        // An empty week ({"data": {}}) simply yields null for every day,
        // which renders as the 'missing' state — never a broken grid.
        this.item = this._timeline[this.formatToYmd(this._day)] || null;
    }

    /**
     * Per-bit completion lookup — supports partial completion (e.g. Stress
     * done, Mood pending, Sleep done). A check-in missing from the payload
     * counts as not completed.
     */
    isCheckinCompleted(name: TimelineCheckin['name']): boolean {
        const checkin = this.item?.checkins?.find(c => c.name === name);
        return checkin?.completed === true;
    }

    get behavioursLabel(): string {
        return this.type === 'carer' ? 'Observed behaviours' : 'Behaviour logs';
    }

    /**
     * Zero-counter entries are excluded — the backend sends a dense array
     * covering every ED-behaviour category every day (e.g. 5 zero-counter
     * entries alongside 1 real occurrence), and listing every zero as its
     * own pill is just noise; only what actually happened is worth a pill.
     * Same convention as cdss-weekly-behaviour-log (*ngIf="... !== 0").
     */
    get behaviours(): TimelineBehaviour[] {
        return (this.item?.behaviours ?? []).filter(behaviour => behaviour.counter !== 0);
    }

    get behavioursState(): BehavioursState {
        if (!this.item || this.item.behaviours == null) return 'missing';
        return this.behaviours.length > 0 ? 'logged' : 'none-today';
    }

    /** Wording of the grey missing-data pill depends on today vs past. */
    get missingBehavioursLabel(): string {
        return this.isToday(this._day) ? 'Not yet today' : 'No check-in';
    }

    isToday(day: Date): boolean {
        if (!day) return false;
        return this.formatToYmd(day) === this.formatToYmd(new Date());
    }

    formatToYmd(date: Date): string {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}${month}${day}`;
    }

    navigateToDailyDetail(): void {
        if (!this.day || !this.patientId) return;
        const route = this.type === 'carer' ? 'carer-daily' : 'patient-daily';
        const y = this.day.getFullYear();
        const m = String(this.day.getMonth() + 1).padStart(2, '0');
        const d = String(this.day.getDate()).padStart(2, '0');
        this.router.navigate(
            ['/cdss/patient-overview', this.patientId, route],
            {queryParams: {date: `${y}-${m}-${d}`}}
        );
    }
}
